import { useContext, useEffect, useState } from "react";
import FileDnd from "../../../components/FileDnD";
import FormField from "../../../components/FormFiel/FormFiel";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { HouseContext } from "../../../context/HouseContext";
import { putProperty } from "../../../application/putProperty";
import { BounceLoader } from "react-spinners";
import {
  deleteImageFromStorage,
  getImagesById,
  saveMultipleImages,
} from "../../../application/saveImg";

function PropertyEdit() {
  const { id } = useParams();
  const { houses } = useContext(HouseContext);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [urlImg, setUrlImg] = useState([]);
  const [fileDragging, setFileDragging] = useState(null);
  const [fileDropping, setFileDropping] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const currency = watch("currency");
  const state = watch("state");

  const handleDrop = (event) => {
    event.preventDefault();
    event.preventDefault();
    const updatedFiles = [...files];
    const [removed] = updatedFiles.splice(fileDragging, 1);
    updatedFiles.splice(fileDropping, 0, removed);

    setFiles(updatedFiles);

    const updatedUrlImg = updatedFiles.map((file) => {
      if (file instanceof File) {
        return { preview: URL.createObjectURL(file) };
      } else {
        return { preview: file.preview }; // Conservar la URL existente
      }
    });

    setUrlImg(updatedUrlImg);
    setFileDragging(null);
    setFileDropping(null);
  };

  const handleDragEnter = (index) => setFileDropping(index);

  const handleDragStart = (index) => setFileDragging(index);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    if (foundProperty) {
      setProperty(foundProperty);
      // Asignar los valores del formulario utilizando setValue
      setValue("name", foundProperty.name);
      setValue("type", foundProperty.type);
      setValue("state", foundProperty.state);
      setValue("country", foundProperty.country);
      setValue("address", foundProperty.address);
      setValue("ambientes", foundProperty.characteristics.ambientes);
      setValue("dormitorios", foundProperty.characteristics.dormitorios);
      setValue("baños", foundProperty.characteristics.baños);
      setValue("patio", foundProperty.characteristics.patio);
      setValue("expensas", foundProperty.characteristics.expensas);
      setValue("estado", foundProperty.characteristics.estado);
      setValue("plantas", foundProperty.characteristics.plantas);
      setValue("contrato", foundProperty.characteristics.contrato);
      setValue("description", foundProperty.description);
      setValue("currency", foundProperty.currency);
      setValue("price", foundProperty.price);
      setValue("maps", foundProperty.maps);
      setValue("highlights", foundProperty.highlights);
    }
  }, [id, houses, setValue]);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    setProperty(foundProperty); // Establece la propiedad encontrada
  }, [id, houses]);

  useEffect(() => {
    if (property) {
      const fetchImages = async () => {
        try {
          const urls = await getImagesById(property.id);
          // Establecer las imágenes en el estado
          setUrlImg(urls.map((url) => ({ preview: url })));
          setFiles(urls.map((url) => ({ preview: url })));
        } catch (error) {
          console.error("Error al obtener las imágenes:", error);
        }
      };
      fetchImages();
    }
  }, [property]);

  if (!property) {
    return (
      <div className="text-center mt-32">
        <h2>Cargando propiedad...</h2>
      </div>
    );
  }

  const handleFiles = (newFiles) => {
    if (Array.isArray(newFiles)) {
      // Obtener las URLs de Firebase Storage para las imágenes existentes
      getImagesById(property.id)
        .then((existingImageUrls) => {
          // Combina las URLs de las imágenes existentes con las nuevas
          const updatedFiles = [
            ...existingImageUrls.map((url) => ({
              preview: url,
              isFirebaseURL: true,
            })),
            ...newFiles.map((file, index) => ({
              preview: URL.createObjectURL(file),
              isFirebaseURL: false,
              file: file, // Guardar el objeto File
            })),
          ];
          // Eliminar duplicados basados en el nombre del archivo
          const uniqueFiles = updatedFiles.filter(
            (file, index, self) =>
              index ===
              self.findIndex((t) =>
                t.isFirebaseURL
                  ? t.preview === file.preview
                  : t.file?.name === file.file?.name
              )
          );
          setFiles(uniqueFiles);
          setUrlImg(uniqueFiles.map((file) => ({ preview: file.preview })));
        })
        .catch((error) => {
          console.error("Error al obtener las imágenes:", error);
        });
    } else {
      console.error("handleFiles: El valor recibido no es un array");
    }
  };

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      // Separar archivos nuevos y existentes
      // Separar archivos nuevos y existentes
      // Separar archivos nuevos y existentes
      // Separar archivos nuevos y existentes
      // Separar archivos nuevos y existentes
      const newFilesToUpload = files.filter(
        (file) => !file.isFirebaseURL && file.file
      );

      // Guardar imágenes nuevas
      const resultStorage =
        newFilesToUpload.length > 0
          ? await saveMultipleImages(
              newFilesToUpload.map((file) => file.file),
              property.id
            )
          : [];

      // Combinar las URLs de las imágenes existentes y las nuevas
      let allImageUrls = [];

      // Agregar las URLs de las imágenes existentes
      if (files && Array.isArray(files)) {
        files.forEach((file) => {
          if (
            file &&
            file.preview &&
            file.preview.startsWith("https://firebasestorage.googleapis.com/")
          ) {
            allImageUrls.push(file.preview);
          }
        });
      }

      // Agregar las URLs de las imágenes recién subidas
      if (resultStorage && Array.isArray(resultStorage)) {
        resultStorage.forEach((url) => {
          allImageUrls.push(url);
        });
      }

      const formattedData = {
        name: formData.name,
        type: formData.type,
        state: formData.state,
        country: formData.country,
        address: formData.address,
        characteristics: {
          ambientes: formData.ambientes || "",
          dormitorios: formData.dormitorios || "",
          baños: formData.baños || "",
          patio: formData.patio || "",
          expensas: formData.expensas || "",
          estado: formData.estado || "",
          plantas: formData.plantas || "",
          contrato: formData.contrato || "",
          superficie: formData.superficie || "",
        },
        description: formData.description,
        currency: formData.currency,
        price: formData.price || (currency === "Consultar" && ""),
        maps: formData.maps,
        highlights: formData.highlights === "true" ? true : false,
        images: allImageUrls,
      };
      //console.log("Datos a enviar a putProperty:", formattedData);
      // Guardar la nueva propiedad
      await putProperty(property.idFirebase, formattedData);
      //console.log("Propiedad guardada con éxito:", formattedData);
    } catch (error) {
      console.error("Error al guardar la propiedad:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFile = async (index) => {
    try {
      // Obtener la URL de la imagen a eliminar
      const fileToRemove = urlImg[index].preview;

      // Eliminar la imagen de Firebase Storage (implementar esta función)
      await deleteImageFromStorage(fileToRemove);

      // Actualizar el estado local
      const updatedUrlImg = [...urlImg];
      updatedUrlImg.splice(index, 1);
      setUrlImg(updatedUrlImg);

      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      setFiles(updatedFiles);
    } catch (error) {
      console.error("Error al eliminar la imagen:", error);
    }
  };

  return (
    <div className="">
      {loading ? (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-20 flex items-center justify-center z-50">
          <div className="absolute inset-0 backdrop-blur-sm"></div>
          <div className="flex flex-col justify-center items-center z-10">
            <BounceLoader className="my-2" color="#F79330" size={60} />
            <h2 className="text-xl">Guardando Propiedad</h2>
          </div>
        </div>
      ) : (
        <section className="sm:w-8/12 p-6 mx-auto bg-white rounded-md shadow-md dark:bg-gray-700">
          <h1 className="text-xl font-bold text-black capitalize dark:text-white">
            Nueva Propiedad
          </h1>
          <div className="relative mt-4 mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 text-gray-500 bg-white">Imagenes</span>
            </div>
          </div>
          <div className="py-4">
            <FileDnd onFilesChange={handleFiles} />
          </div>

          <div className="relative mt-4 mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 text-gray-500 bg-white">
                Imagenes Publicadas
              </span>
            </div>
          </div>
          {urlImg.length > 0 && (
            <div
              className="grid grid-cols-2 gap-4 mt-4 md:grid-cols-6"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              {urlImg.map((file, index) => (
                <div
                  key={index}
                  className={`relative flex flex-col items-center overflow-hidden text-center bg-gray-100 border rounded cursor-move select-none ${
                    fileDragging === index ? "border-blue-600" : ""
                  }`}
                  style={{ paddingTop: "100%" }}
                  draggable="true"
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                >
                  <button
                    className="absolute top-0 right-0 z-50 p-1 bg-white rounded-bl focus:outline-none"
                    type="button"
                    onClick={() => removeFile(index)}
                  >
                    <svg
                      className="w-4 h-4 text-gray-700"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <img
                    className="absolute inset-0 z-0 object-cover w-full h-full border-4 border-white preview"
                    src={file.preview}
                    alt={file.name}
                  />
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Datos */}
            <div className="relative mt-4 mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">Datos</span>
              </div>
            </div>
            <FormField
              label="Nombre"
              id="name"
              name="nombre"
              type="text"
              required={true}
              maxLength={50}
              placeholder="Nombre"
              autoComplete="off"
              errors={errors}
              register={register}
            />
            <div className="grid grid-cols-1 gap-6 mt-4 sm:grid-cols-2">
              <FormField
                label="Tipo"
                id="type"
                required={true}
                name="tipo"
                type="select"
                errors={errors}
                register={register}
                options={[
                  { value: "", label: "Seleccionar tipo" },
                  { value: "ApartHotel", label: "Apart hotel" },
                  { value: "Baulera", label: "Baulera" },
                  { value: "Cabanha", label: "Cabaña" },
                  { value: "Campo", label: "Campo" },
                  { value: "Casa", label: "Casa" },
                  { value: "Chacra", label: "Chacra" },
                  { value: "Chalet", label: "Chalet" },
                  { value: "Cochera", label: "Cochera" },
                  { value: "ComplejoTuristico", label: "Complejo turístico" },
                  { value: "Consultorio", label: "Consultorio" },
                  { value: "Departamento", label: "Departamento" },
                  { value: "Duplex", label: "Dúplex" },
                  { value: "FondoDeComercio", label: "Fondo de comercio" },
                  { value: "Fraccion", label: "Fracción" },
                  { value: "Galpon", label: "Galpón / Depósito" },
                  { value: "Hosteria", label: "Hostería" },
                  { value: "Local", label: "Local" },
                  { value: "Lodge", label: "Lodge" },
                  { value: "Loft", label: "Loft" },
                  { value: "Lote", label: "Lote" },
                  { value: "NaveIndustrial", label: "Nave industrial" },
                  { value: "Oficina", label: "Oficina" },
                  { value: "PH", label: "PH" },
                  { value: "Posada", label: "Posada" },
                  { value: "Predio", label: "Predio" },
                  { value: "Quinta", label: "Quinta" },
                  { value: "Terreno", label: "Terreno" },
                ]}
              />

              <FormField
                label="Estado"
                id="state"
                required={true}
                name="estado"
                type="select"
                errors={errors}
                register={register}
                options={[
                  { value: "", label: "Seleccionar estado" },
                  { value: "Alquiler", label: "Alquiler" },
                  { value: "Alquiler Temporal", label: "Alquiler Temporal" },
                  { value: "Alquilado", label: "Alquilado" },
                  { value: "Venta", label: "Venta" },
                  { value: "Vendido", label: "Vendido" },
                ]}
              />
            </div>

            <div className="relative mt-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">Ubicación</span>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 mt-4 sm:grid-cols-2">
              <FormField
                label="Localidad"
                id="country"
                name="localidad"
                required={true}
                type="text"
                maxLength={20}
                placeholder="Localidad"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />

              <FormField
                label="Dirección"
                id="address"
                name="dirección"
                required={true}
                type="text"
                maxLength={45}
                placeholder="Dirección"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
            </div>
            <div className="relative mt-4 mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">
                  Caracteristicas
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 mt-4 sm:grid-cols-2">
              <FormField
                label="Cantidad de Ambientes"
                id="ambientes"
                name="ambientes"
                required={false}
                type="number"
                maxLength={10}
                placeholder="Ambientes"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
              <FormField
                label="Cantidad de Dormitorios"
                id="dormitorios"
                name="dormitorios"
                required={false}
                type="number"
                maxLength={10}
                placeholder="Dormitorios"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
              <FormField
                label="Cantidad de Baños"
                id="baños"
                name="baños"
                required={false}
                type="number"
                maxLength={10}
                placeholder="Baños"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
              <FormField
                label="¿Tiene patio?"
                id="patio"
                required={false}
                name="patio"
                type="select"
                errors={errors}
                register={register}
                options={[
                  { value: "", label: "Seleccionar patio" },
                  { value: "Si", label: "Si" },
                  { value: "No", label: "No" },
                ]}
              />
              <FormField
                label="¿Tiene expensas?"
                id="expensas"
                required={false}
                name="expensas"
                type="select"
                errors={errors}
                register={register}
                options={[
                  { value: "", label: "Seleccionar expesas" },
                  { value: "Si", label: "Si" },
                  { value: "No", label: "No" },
                ]}
              />
              <FormField
                label="Estado del inmueble"
                id="estado"
                required={false}
                name="estado"
                type="select"
                errors={errors}
                register={register}
                options={[
                  { value: "", label: "Seleccionar estado" },
                  { value: "Nuevo", label: "Nuevo" },
                  { value: "Usado", label: "Usado" },
                  { value: "Bueno", label: "Bueno" },
                  { value: "Muy Bueno", label: "Muy Bueno" },
                  { value: "Exelente", label: "Exelente" },
                ]}
              />
              <FormField
                label="Cantidad de Plantas"
                id="plantas"
                name="plantas"
                required={false}
                type="number"
                maxLength={10}
                placeholder="Plantas"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
              <FormField
                label="Contrato"
                id="contrato"
                name="contrato"
                required={false}
                type="text"
                maxLength={50}
                placeholder="Contrato"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
              <FormField
                label="Superficie"
                id="superficie"
                name="superficie"
                required={false}
                type="number"
                maxLength={10}
                placeholder="Superficie"
                autoComplete={"off"}
                errors={errors}
                register={register}
              />
            </div>
            <div className="relative mt-4 mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">Descripción</span>
              </div>
            </div>

            <FormField
              label="Descripción"
              id="description"
              name="descripcion"
              required={true}
              type="textarea"
              rows={8}
              placeholder="Description aquí..."
              errors={errors}
              register={register}
            />

            <div className="relative mt-4 mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">Ubicación</span>
              </div>
            </div>

            <FormField
              label="URL de Google Maps"
              id="maps"
              name="mapa"
              required={true}
              type="text"
              placeholder="URL de Google Maps"
              autoComplete={"off"}
              errors={errors}
              register={register}
            />

            <div className="relative mt-4 mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500 bg-white">
                  Destacar Propiedad
                </span>
              </div>
            </div>

            <FormField
              label="Destacar Propiedad"
              id="highlights"
              required={false}
              name="destacar propiedad"
              type="select"
              errors={errors}
              register={register}
              options={[
                { value: "", label: "Destacar propiedad" },
                { value: true, label: "Si" },
                { value: false, label: "No" },
              ]}
            />
            <p className="text-xs text-slate-500 mt-2">
              Este campo te permite dar mayor visibilidad a una propiedad en la
              lista de resultados. Si seleccionas "Sí", la propiedad aparecerá
              arriba del todo en la lista, resaltándola sobre las demás.
            </p>

            {state !== "Alquilado" && state !== "Vendido" && (
              <>
                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 text-gray-500 bg-white">Precio</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 mt-4 sm:grid-cols-2">
                  <FormField
                    label="Moneda"
                    id="currency"
                    required={true}
                    name="moneda"
                    type="select"
                    errors={errors}
                    register={register}
                    options={[
                      { value: "", label: "Seleccionar moneda" },
                      { value: "$", label: "$" },
                      { value: "USD", label: "USD" },
                      { value: "Consultar", label: "Consultar" },
                    ]}
                  />

                  {currency !== "Consultar" && (
                    <FormField
                      label="Precio"
                      id="price"
                      name="precio"
                      required={true}
                      type="number"
                      maxLength={10}
                      placeholder="Precio"
                      autoComplete={"off"}
                      errors={errors}
                      register={register}
                    />
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end mt-6">
              <button
                type="submit"
                className="px-6 py-2 leading-5 text-white transition-colors duration-200 transform bg-primary hover:opacity-80 rounded-md"
              >
                Guardar
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}

export default PropertyEdit;
